import { useRMSelector } from 'redux/hooks';
import { useAllSelectedWellDrawingsByType } from './well';
import { CrystalDrawing, DrawingType } from 'components/well/drawingManager/constants';
import { useMemo } from 'react';
import { useLastSelectedWellNavigationDetails, useSelectedDropImageParameters } from './experiment';

export const useSoakings = () => {
  return useRMSelector((state) => state?.soakings);
};

export const useCryoprotectants = () => {
  return useRMSelector((state) => state?.cryoprotectants);
};

export const useSynchTrip = () => {
  return useRMSelector((state) => state?.synchrotronTrip);
};

export const useHarvestingConentView = () => {
  return useRMSelector((state) => state?.synchrotronTrip?.harvestingContentView);
};

export const useCrystalTrips = () => {
  return useRMSelector((state) => state?.synchrotronTrip?.crystalsTrips);
};

export const useSelectedTripsCrystals = () => {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const {wellNum, plateID} = wellNavigationDetails || {};
  const {dropNumber, regionName} = selectedDropImageParameters || {};
  const allCrystals = useAllSelectedWellDrawingsByType(
    plateID,
    wellNum,
    dropNumber,
    regionName,
    DrawingType.CRYSTAL,
  ) as CrystalDrawing[];
  const selectedTripCrystalIds = useRMSelector(
    (state) => state?.synchrotronTrip?.selectedTripCrystalIds,
  );
  return useMemo(() => {
    const selectedTripCrystalIdsSet = new Set(selectedTripCrystalIds);
    return allCrystals?.filter((crystal) => selectedTripCrystalIdsSet.has(crystal.id));
  }, [allCrystals, selectedTripCrystalIds]);
};

export const useSelectedTripId = () => {
  return useRMSelector((state) => state?.synchrotronTrip?.selectedTripId);
};

export const useAllSynchTrips = () => {
  return useRMSelector((state) => state?.synchrotronTrip?.allSynchTrips);
};

export const useSelectedCrystal = () => {
  const crystal = useRMSelector((state) =>
    state?.synchrotronTrip?.editedSynchTrip?.assignedCrystals.find(
      (item) => item.id === state?.synchrotronTrip?.crystalId,
    ),
  );
  return crystal;
};

export const useCrystals = () => {
  return useRMSelector(
    (state) => state?.synchrotronTrip?.editedSynchTrip?.assignedCrystals,
  );
};

export const useSelectedCrystalId = () => {
  return useRMSelector((state) => state?.synchrotronTrip?.crystalId);
};

export const useAllPucks = () => {
  return useRMSelector((state) => state.pucks);
};

export const usePuckById = (puckId: number) => {
  return useRMSelector((state) => state.pucks?.find((item) => item.id === puckId));
};

export const useAllDewars = () => {
  return useRMSelector((state) => state.dewars);
};

export const useDewarById = (dewarId: number) => {
  return useRMSelector((state) => state.dewars?.find((item) => item.id === dewarId));
};

export const useTripEditStatus = () => {
  return useRMSelector((state) => state.synchrotronTrip.editedSynchTrip.editStatus);
};

export const useIsCrystalAssignmentsDirty = () => {
  return useRMSelector(
    (state) => state.synchrotronTrip.editedSynchTrip.isCrystalAssignmentsDirty,
  );
};

export const useEditedSynchTrip = () => {
  return useRMSelector((state) => state.synchrotronTrip.editedSynchTrip);
};

export const useOriginalSynchTrip = () => {
  return useRMSelector((state) => state.synchrotronTrip.originalSynchTrip);
};
